import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import {
  getRegistrationPlaceCode,
  isCancelledRegistrationStatus,
  normalizePlaceCode,
} from '../../../utils/registrationPlaceCode';
import './CabinUtilization.css';

interface CabinUtilizationRow {
  placeCode: string;
  placeName: string;
  accommodationType: string;
  camperCount: number;
  capacity: number;
}

type SortField = 'placeName' | 'placeCode' | 'accommodationType' | 'camperCount' | 'capacity';
type SortDirection = 'asc' | 'desc';

const formatAccommodationType = (value: any): string => {
  const type = String(value || '').trim();
  if (!type || type === 'Standard') return 'UCCMA';
  if (type === 'Private' || type === 'Staff' || type === 'Funded' || type === 'Donated') return type;
  return type;
};

const CabinUtilization: React.FC = () => {
  const navigate = useNavigate();
  const [cottages, setCottages] = useState<CabinUtilizationRow[]>([]);
  const [privateCottages, setPrivateCottages] = useState<CabinUtilizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('placeName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  useEffect(() => {
    const fetchCabinUtilization = async () => {
      try {
        setLoading(true);
        setError(null);

        const [inventorySnap, registrationsSnap] = await Promise.all([
          getDoc(doc(db, 'inventory', 'accommodations')),
          getDocs(collection(db, 'Registrations')),
        ]);

        if (!inventorySnap.exists()) {
          setError('No accommodations inventory found');
          setCottages([]);
          setPrivateCottages([]);
          return;
        }

        const inventoryData = inventorySnap.data();
        const camperCountsByPlaceCode: Record<string, number> = {};

        registrationsSnap.forEach((regDoc) => {
          const data = regDoc.data();
          const placeCode = getRegistrationPlaceCode(data);
          if (!placeCode) return;

          // Cancelled registrations contribute 0 campers.
          if (isCancelledRegistrationStatus(data.status)) return;

          const attendingCampers = Array.isArray(data.attendingCampers)
            ? data.attendingCampers.length
            : 0;

          camperCountsByPlaceCode[placeCode] =
            (camperCountsByPlaceCode[placeCode] || 0) + attendingCampers;
        });

        const buildRows = (units: any[]): CabinUtilizationRow[] =>
          (Array.isArray(units) ? units : []).map((unit) => {
            const placeCode = normalizePlaceCode(unit?.placeCode);
            return {
              placeCode,
              placeName: unit?.placeName || unit?.description || placeCode || 'Unnamed',
              accommodationType: formatAccommodationType(unit?.accommodationType),
              camperCount: placeCode ? camperCountsByPlaceCode[placeCode] || 0 : 0,
              capacity: typeof unit?.capacity === 'number' ? unit.capacity : Number(unit?.capacity) || 0,
            };
          });

        setCottages(buildRows(inventoryData.Cottage));
        setPrivateCottages(buildRows(inventoryData.privateCottage));
      } catch (err) {
        console.error('Error fetching cabin utilization:', err);
        setError('Failed to load cabin utilization report');
      } finally {
        setLoading(false);
      }
    };

    fetchCabinUtilization();
  }, []);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedRows = (rows: CabinUtilizationRow[]) => {
    return [...rows].sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return a.placeCode.localeCompare(b.placeCode);
    });
  };

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleRowClick = (placeCode: string) => {
    if (!placeCode) return;
    navigate(`/admin/inventory?placeCode=${encodeURIComponent(placeCode)}&select=true`);
  };

  const renderSection = (title: string, rows: CabinUtilizationRow[]) => {
    const sorted = getSortedRows(rows);
    const totalCampers = rows.reduce((sum, row) => sum + row.camperCount, 0);
    const totalCapacity = rows.reduce((sum, row) => sum + row.capacity, 0);

    return (
      <div className="cabin-utilization-section" style={{ marginBottom: 32 }}>
        <h3>
          {title}
          <span style={{ fontWeight: 400, fontSize: '0.85em', marginLeft: 8, color: '#666' }}>
            ({rows.length} units · {totalCampers} / {totalCapacity} campers)
          </span>
        </h3>

        {rows.length === 0 ? (
          <div className="no-data">No {title.toLowerCase()} found in inventory.</div>
        ) : (
          <table className="stats-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('placeName')} style={{ cursor: 'pointer' }}>
                  Place Name{sortIndicator('placeName')}
                </th>
                <th onClick={() => handleSort('placeCode')} style={{ cursor: 'pointer' }}>
                  Place Code{sortIndicator('placeCode')}
                </th>
                <th onClick={() => handleSort('accommodationType')} style={{ cursor: 'pointer' }}>
                  Accommodation Type{sortIndicator('accommodationType')}
                </th>
                <th onClick={() => handleSort('camperCount')} style={{ cursor: 'pointer' }}>
                  Campers / Capacity{sortIndicator('camperCount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr
                  key={row.placeCode || row.placeName}
                  className="cabin-utilization-row"
                  onClick={() => handleRowClick(row.placeCode)}
                  title={row.placeCode ? `Open ${row.placeCode} in Inventory` : undefined}
                >
                  <td>{row.placeName}</td>
                  <td>{row.placeCode || '—'}</td>
                  <td>{row.accommodationType}</td>
                  <td>
                    {row.camperCount} / {row.capacity}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = () => {
    const escape = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
    const headers = ['Section', 'Place Name', 'Place Code', 'Accommodation Type', 'Campers', 'Capacity'];

    const toRows = (section: string, rows: CabinUtilizationRow[]) =>
      getSortedRows(rows).map((row) =>
        [
          escape(section),
          escape(row.placeName),
          escape(row.placeCode),
          escape(row.accommodationType),
          escape(row.camperCount),
          escape(row.capacity),
        ].join(',')
      );

    const csvContent = [
      headers.join(','),
      ...toRows('Cottages', cottages),
      ...toRows('Private Cottages', privateCottages),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cabin-utilization-${new Date().toISOString().split('T')[0]}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div>Loading cabin utilization...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="cabin-utilization-report">
      <div className="cabin-utilization-header">
        <h2>Cabin Utilization</h2>
        <div className="cabin-utilization-actions">
          <button type="button" onClick={handlePrint} className="print-button">
            Print Report
          </button>
          <button type="button" onClick={handleExportCSV} className="export-button">
            Export to CSV
          </button>
        </div>
      </div>
      <p className="report-description">
        Registered campers per cottage and private cottage, including empty units.
        Cancelled registrations count as zero.
      </p>

      {renderSection('Cottages', cottages)}
      {renderSection('Private Cottages', privateCottages)}
    </div>
  );
};

export default CabinUtilization;
